import { useLayoutEffect, useRef } from "react";
import * as styles from "./Table.css";

export type Measurement = {
	column: string;
	width: number;
	left: number;
	right: number;
};
export type TableProps = {
	headers?: string[];
	highlightColumns?: string[];
	data: string[][];
	activeIndex?: number;
	className?: string;
	style?: React.CSSProperties;
	cellClasses?: (cell: [number, number]) => string;
	headerClasses?: (header: number) => string;
	onColumnMeasurements?: (measurements: Measurement[]) => void;
	renderCell?: (value: unknown, cell: [number, number]) => React.ReactNode;
	renderHeader?: (value: unknown, header: number) => React.ReactNode;
};

export default function Table({
	headers,
	highlightColumns,
	data,
	className,
	cellClasses,
	headerClasses,
	onColumnMeasurements,
	renderCell = (x, _cell) => x as string,
	renderHeader = (x, _cell) => x as string,
	style,
	scroll,
}: TableProps & styles.WrapVariants) {
	const headerRefs = useRef<Record<string, HTMLTableCellElement | null>>({});
	const tableRef = useRef<HTMLTableElement | null>(null);
	const measureColumns = (): void => {
		if (!onColumnMeasurements || !highlightColumns) return;

		const measurements = highlightColumns
			.map((colName) => {
				const tableEl = tableRef.current;
				const el = headerRefs.current[colName];
				if (!el) return null;
				if (!tableEl) return null;

				const rect = el.getBoundingClientRect();
				const tableRect = tableEl.getBoundingClientRect();
				return {
					column: colName,
					left: rect.left - tableRect.left,
					right: tableRect.right - rect.right,
					width: rect.width,
				};
			})
			.filter((item): item is Measurement => item !== null);

		onColumnMeasurements(measurements);
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: need to run this hook whenever data changes
	useLayoutEffect(() => {
		measureColumns();

		const observer = new ResizeObserver(() => {
			measureColumns();
		});

		if (tableRef.current) {
			observer.observe(tableRef.current);
		}

		return () => {
			observer.disconnect();
		};
	}, [headers, data, highlightColumns, onColumnMeasurements]);
	return (
		<div className={styles.wrap({ scroll })}>
			<table
				ref={tableRef}
				className={`${styles.table} ${className}`}
				style={style}
				data-row-count={data.length}
			>
				{headers && (
					<thead>
						<tr className={styles.headerRow}>
							{headers.map((header, index) => {
								const key = `header-${index}`;
								const renderedHeader = renderHeader(header, index);
								return (
									<th
										key={key}
										className={`${styles.headerCell} ${headerClasses?.(index)}`}
										ref={(el) => {
											if (el) {
												headerRefs.current[header] = el;
											}
										}}
									>
										{index === 0 ? (
											<div className={styles.scrollShadow({ scroll })}>
												{renderedHeader}
											</div>
										) : (
											renderedHeader
										)}
									</th>
								);
							})}
						</tr>
					</thead>
				)}
				<tbody>
					{data.map((rowData, rowIndex) => {
						const key = `row-${rowIndex}`;
						return (
							<tr key={key} className={styles.row}>
								{rowData.map((cellData, colIndex) => {
									const key = `cell-${rowIndex}-${colIndex}`;
									const renderedCell = renderCell(cellData, [
										rowIndex,
										colIndex,
									]);

									return (
										<td
											key={key}
											className={`${styles.cell} ${cellClasses?.([rowIndex, colIndex])}`}
											data-value={cellData}
										>
											{colIndex === 0 ? (
												<div className={styles.scrollShadow({ scroll })}>
													{renderedCell}
												</div>
											) : (
												renderedCell
											)}
										</td>
									);
								})}
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}
